import { Coding, Organization } from "@medplum/fhirtypes";

import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";

import { PRACTICE_FEATURE_FLAG_VALUES } from "../constants/feature-flags";
import { FeatureFlag } from "../types/feature-flag";

/**
 * Returns a copy of the organization with the flag's tag appended.
 *
 * @param organization - Organization to tag
 * @param flag - Practice or development feature flag to enable
 * @returns The tagged Organization, or `undefined` when the flag is already on it
 */
export function addFeatureFlagTag(
  organization: Organization,
  flag: FeatureFlag,
): Organization | undefined {
  const flagTag = toFeatureFlagTag(flag);
  const flagKey = featureTagKey(flagTag);
  const tags = organization.meta?.tag ?? [];
  if (tags.some((tag) => featureTagKey(tag) === flagKey)) return undefined;

  return { ...organization, meta: { ...organization.meta, tag: [...tags, flagTag] } };
}

/**
 * Returns a copy of the organization without the flag's tag. Every other tag is kept.
 *
 * @param organization - Organization to untag
 * @param flag - Practice or development feature flag to disable
 * @returns The untagged Organization, or `undefined` when the flag is not on it
 */
export function removeFeatureFlagTag(
  organization: Organization,
  flag: FeatureFlag,
): Organization | undefined {
  const flagKey = featureTagKey(toFeatureFlagTag(flag));
  const tags = organization.meta?.tag ?? [];
  const remainingTags = tags.filter((tag) => featureTagKey(tag) !== flagKey);
  if (remainingTags.length === tags.length) return undefined;

  return { ...organization, meta: { ...organization.meta, tag: remainingTags } };
}

/**
 * Identifies a tag by its system and code, so two codings for the same flag compare equal.
 *
 * @param tag - Coding to identify
 * @returns The `system|code` key
 */
export function featureTagKey(tag: Coding): string {
  return `${tag.system ?? ""}|${tag.code ?? ""}`;
}

/** Builds the `meta.tag` coding for a flag, under its practice or development system. */
function toFeatureFlagTag(flag: FeatureFlag): Coding {
  const system = PRACTICE_FEATURE_FLAG_VALUES.has(flag)
    ? QuiltedHealthCodeSystems.PracticeFeatureFlags
    : QuiltedHealthCodeSystems.DevelopmentFeatureFlags;
  return { system, code: flag };
}
